import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } =>
    typeof s.redirect === "string" ? { redirect: s.redirect } : {},
  head: () => ({ meta: [{ title: "Sign in · CARIO INTEL" }] }),
  component: LoginPage,
});

function LoginPage() {
  const { session, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && session) window.location.assign(search.redirect || "/dashboard");
  }, [loading, session, navigate, search.redirect]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
      window.location.assign(search.redirect || "/dashboard");
    } catch (e: any) {
      toast.error(e?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/request-access" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Request access →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Secure sign-in</p>
          <h1 className="mt-2 font-display text-3xl">Operator console</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Access is restricted to authorized analysts.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="analyst@org.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-[11px] text-cyan hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" autoComplete="current-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Working…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            No account? <Link to="/signup" className="text-cyan hover:underline">Create one</Link> · <Link to="/pricing" className="text-cyan hover:underline">View plans</Link>
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Have an invitation? <Link to="/accept-invite" className="text-cyan hover:underline">Activate it →</Link>
          </p>
          <p className="mt-4 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
            <Link to="/admin-login" className="hover:text-cyan">Administrator sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}